import { useEffect, useState } from 'react'
import { useToast } from './ToastProvider'
import { migrateAnonymousCustomizations } from '../utils/customizationStorage'
import { useSupabaseAuth } from '../hooks/useSupabaseAuth.js'

function shouldLogMigrationDebug() {
  if (import.meta?.env?.DEV === true || import.meta?.env?.VITE_DEBUG_LOGS === 'true') return true
  try {
    return window.localStorage?.getItem('ff.debug.migration') === '1'
  } catch {
    return false
  }
}

const CustomizationMigration = () => {
  const { user, isSignedIn, getToken } = useSupabaseAuth()
  const { addToast } = useToast()
  const [migrating, setMigrating] = useState(false)
  const [migrated, setMigrated] = useState(false)

  useEffect(() => {
    const handleMigration = async () => {
      const debug = shouldLogMigrationDebug()
      if (debug) {
        console.log('CustomizationMigration: Checking migration conditions', {
          isSignedIn,
          hasUser: !!user,
          migrated,
          migrating,
          userId: user?.id
        })
      }
      
      // Only migrate if user is signed in and we haven't migrated yet
      if (isSignedIn && user && !migrated && !migrating) {
        if (debug) console.log('CustomizationMigration: Starting migration for user:', user.id)
        setMigrating(true)
        
        try {
          const token = await getToken()
          if (debug) console.log('CustomizationMigration: Got token, starting migration')
          const migratedCustomizations = await migrateAnonymousCustomizations(user.id, token)
          
          if (debug) console.log('CustomizationMigration: Migration result:', migratedCustomizations)
          
          if (migratedCustomizations.length > 0) {
            // Toast removed - unnecessary UX clutter
            
            // Trigger a page reload to ensure customizations are restored
            // This is especially important if the user is on a customization page
            const currentPath = window.location.pathname
            if (currentPath.includes('/customize/')) {
              if (debug) console.log('User is on customization page, triggering reload to restore customizations')
              // Small delay to ensure the toast is shown before reload
              setTimeout(() => {
                window.location.reload()
              }, 1500)
            }
            
            // Don't redirect - let the user stay on their current page
            // The customizations are now saved to their account and can be accessed
            // If they're on a customization page, their work is preserved
            if (debug) console.log('Customizations migrated successfully:', migratedCustomizations.length)
          } else {
            if (debug) console.log('No customizations were migrated')
          }
          
          setMigrated(true)
        } catch (error) {
          console.error('Failed to migrate customizations:', error)
          addToast({
            type: 'error',
            title: 'Migration Failed',
            message: 'Failed to restore your customizations. You can still access them from your account.'
          })
          setMigrated(true)
        } finally {
          setMigrating(false)
        }
      }
    }

    handleMigration()
  }, [isSignedIn, user, migrated, migrating, getToken, addToast])

  // Don't render anything - this is a background component
  return null
}

export default CustomizationMigration
